import type { Dexie, Transaction } from 'dexie'
import { migrationWrite } from './syncBookkeeping'

/**
 * **Das Schema der lokalen Datenbank** — alle Versionen und ihre Migrationen,
 * aus dem Konstruktor von `HappyTaskDB` gezogen (2026-09-05, siehe
 * `.claude/tasks/large-files.md`). CLAUDE.md nannte diese Datei schon, bevor
 * es sie gab.
 *
 * **Eine Version wird nie geändert, nur eine neue angehängt.** Dexie führt
 * jede Datenbank über die Kette der `upgrade`-Rückrufe an den heutigen Stand;
 * wer eine alte Stufe umschreibt, ändert die Vergangenheit von Geräten, die
 * dort noch stehen.
 *
 * Aufgerufen im Konstruktor, mit `this` als `db`.
 */
export function defineSchema(db: Dexie): void {
    db.version(2).stores({
      tasks: 'id, title, status, priority, category, dueDate, completedAt, createdAt, updatedAt, lastModified, synced, parentTaskId, order',
      settings: 'id, key, type, isUserSetting, createdAt, updatedAt, lastModified, synced',
      userProfiles: 'id, username, email, fullName, createdAt, updatedAt, lastModified, synced',
      timeEntries: 'id, taskId, startTime, endTime, createdAt, updatedAt, lastModified, synced',
      tags: 'id, name, usageCount, createdAt, updatedAt, lastModified, synced',
      projects: 'id, name, status, startDate, endDate, createdAt, updatedAt, lastModified, synced',
      syncMetadata: 'id, tableName, entityId, localVersion, remoteVersion, conflictStatus, createdAt'
    })

    // Version 3: Add avatar tables
    db.version(3).stores({
      tasks: 'id, title, status, priority, category, dueDate, completedAt, createdAt, updatedAt, lastModified, synced, parentTaskId, order',
      settings: 'id, key, type, isUserSetting, createdAt, updatedAt, lastModified, synced',
      userProfiles: 'id, username, email, fullName, createdAt, updatedAt, lastModified, synced',
      timeEntries: 'id, taskId, startTime, endTime, createdAt, updatedAt, lastModified, synced',
      tags: 'id, name, usageCount, createdAt, updatedAt, lastModified, synced',
      projects: 'id, name, status, startDate, endDate, createdAt, updatedAt, lastModified, synced',
      syncMetadata: 'id, tableName, entityId, localVersion, remoteVersion, conflictStatus, createdAt',
      avatarCategories: 'id, sort_order, is_active',
      avatars: 'id, category_id, is_preset, is_active'
    })

    // Version 4: Add cached image support for avatars
    db.version(4).stores({
      tasks: 'id, title, status, priority, category, dueDate, completedAt, createdAt, updatedAt, lastModified, synced, parentTaskId, order',
      settings: 'id, key, type, isUserSetting, createdAt, updatedAt, lastModified, synced',
      userProfiles: 'id, username, email, fullName, createdAt, updatedAt, lastModified, synced',
      timeEntries: 'id, taskId, startTime, endTime, createdAt, updatedAt, lastModified, synced',
      tags: 'id, name, usageCount, createdAt, updatedAt, lastModified, synced',
      projects: 'id, name, status, startDate, endDate, createdAt, updatedAt, lastModified, synced',
      syncMetadata: 'id, tableName, entityId, localVersion, remoteVersion, conflictStatus, createdAt',
      avatarCategories: 'id, sort_order, is_active',
      avatars: 'id, category_id, is_preset, is_active, cached_at'
    })

    // Version 5: Add lifeSpace, userId, cloudId, order, deletedAt to projects
    db.version(5).stores({
      tasks: 'id, title, status, priority, category, dueDate, completedAt, createdAt, updatedAt, lastModified, synced, parentTaskId, order',
      settings: 'id, key, type, isUserSetting, createdAt, updatedAt, lastModified, synced',
      userProfiles: 'id, username, email, fullName, createdAt, updatedAt, lastModified, synced',
      timeEntries: 'id, taskId, startTime, endTime, createdAt, updatedAt, lastModified, synced',
      tags: 'id, name, usageCount, createdAt, updatedAt, lastModified, synced',
      projects: 'id, name, status, lifeSpace, userId, cloudId, order, createdAt, updatedAt, lastModified, synced',
      syncMetadata: 'id, tableName, entityId, localVersion, remoteVersion, conflictStatus, createdAt',
      avatarCategories: 'id, sort_order, is_active',
      avatars: 'id, category_id, is_preset, is_active, cached_at'
    })

    // Version 6: Add projectId, userId, cloudId, deletedAt to tasks
    db.version(6).stores({
      tasks: 'id, title, status, priority, category, projectId, userId, cloudId, dueDate, completedAt, createdAt, updatedAt, lastModified, synced, parentTaskId, order',
      settings: 'id, key, type, isUserSetting, createdAt, updatedAt, lastModified, synced',
      userProfiles: 'id, username, email, fullName, createdAt, updatedAt, lastModified, synced',
      timeEntries: 'id, taskId, startTime, endTime, createdAt, updatedAt, lastModified, synced',
      tags: 'id, name, usageCount, createdAt, updatedAt, lastModified, synced',
      projects: 'id, name, status, lifeSpace, userId, cloudId, order, createdAt, updatedAt, lastModified, synced',
      syncMetadata: 'id, tableName, entityId, localVersion, remoteVersion, conflictStatus, createdAt',
      avatarCategories: 'id, sort_order, is_active',
      avatars: 'id, category_id, is_preset, is_active, cached_at'
    })

    // Version 7: Add syncQueue table (consolidated from old HappyTaskDB_v3)
    db.version(7).stores({
      tasks: 'id, title, status, priority, category, projectId, userId, cloudId, dueDate, completedAt, createdAt, updatedAt, lastModified, synced, parentTaskId, order',
      settings: 'id, key, type, isUserSetting, createdAt, updatedAt, lastModified, synced',
      userProfiles: 'id, username, email, fullName, createdAt, updatedAt, lastModified, synced',
      timeEntries: 'id, taskId, startTime, endTime, createdAt, updatedAt, lastModified, synced',
      tags: 'id, name, usageCount, createdAt, updatedAt, lastModified, synced',
      projects: 'id, name, status, lifeSpace, userId, cloudId, order, createdAt, updatedAt, lastModified, synced',
      syncMetadata: 'id, tableName, entityId, localVersion, remoteVersion, conflictStatus, createdAt',
      syncQueue: '++id, synced, timestamp, table',
      avatarCategories: 'id, sort_order, is_active',
      avatars: 'id, category_id, is_preset, is_active, cached_at'
    })

    // Version 8: Add sections table + sectionId on tasks
    db.version(8).stores({
      tasks: 'id, title, status, priority, category, projectId, sectionId, userId, cloudId, dueDate, completedAt, createdAt, updatedAt, lastModified, synced, parentTaskId, order',
      sections: 'id, projectId, order, deletedAt',
      settings: 'id, key, type, isUserSetting, createdAt, updatedAt, lastModified, synced',
      userProfiles: 'id, username, email, fullName, createdAt, updatedAt, lastModified, synced',
      timeEntries: 'id, taskId, startTime, endTime, createdAt, updatedAt, lastModified, synced',
      tags: 'id, name, usageCount, createdAt, updatedAt, lastModified, synced',
      projects: 'id, name, status, lifeSpace, userId, cloudId, order, createdAt, updatedAt, lastModified, synced',
      syncMetadata: 'id, tableName, entityId, localVersion, remoteVersion, conflictStatus, createdAt',
      syncQueue: '++id, synced, timestamp, table',
      avatarCategories: 'id, sort_order, is_active',
      avatars: 'id, category_id, is_preset, is_active, cached_at'
    })

    // Version 9: Add cloudId + synced indexes to sections (required by sync-pull duplicate guard)
    db.version(9).stores({
      sections: 'id, projectId, cloudId, order, synced, deletedAt',
    })

    // Version 10: Add cloudId, userId, deletedAt to tags for sync support
    db.version(10).stores({
      tags: 'id, name, usageCount, userId, cloudId, deletedAt, createdAt, updatedAt, lastModified, synced',
    })

    // Version 11: Backfill startDate/startTime/startPeriod on existing tasks
    db.version(11).stores({}).upgrade(tx => {
      return migrationWrite(() => tx.table('tasks').toCollection().modify(task => {
        if (!('startDate' in task)) task.startDate = null
        if (!('startTime' in task)) task.startTime = null
        if (!('startPeriod' in task)) task.startPeriod = null
      }))
    })

    // Version 12: Backfill dueTime/duePeriod on existing tasks
    db.version(12).stores({}).upgrade(tx => {
      return migrationWrite(() => tx.table('tasks').toCollection().modify(task => {
        if (!('dueTime' in task)) task.dueTime = null
        if (!('duePeriod' in task)) task.duePeriod = null
      }))
    })

    // Version 13: Backfill isSystem=false / systemKind=null on existing projects
    db.version(13).stores({}).upgrade(tx => {
      return migrationWrite(() => tx.table('projects').toCollection().modify(project => {
        if (!('isSystem' in project)) project.isSystem = false
        if (!('systemKind' in project)) project.systemKind = null
      }))
    })

    // Version 14: Backfill pausedAt=null on existing tasks (Clarify-Mode "Ruhen lassen").
    // No new stores() needed — column is not indexed (app only writes it, doesn't query).
    db.version(14).stores({}).upgrade(tx => {
      return migrationWrite(() => tx.table('tasks').toCollection().modify(task => {
        if (!('pausedAt' in task)) task.pausedAt = null
      }))
    })

    // Version 15: Index `startDate` on tasks so the UpcomingPage can do
    // range-scoped queries (`.where('startDate').between(startMonth, endMonth)`)
    // instead of a full-table scan. `dueDate` was already indexed.
    db.version(15).stores({
      tasks: 'id, title, status, priority, category, projectId, sectionId, userId, cloudId, startDate, dueDate, completedAt, createdAt, updatedAt, lastModified, synced, parentTaskId, order',
    })

    // Version 16: Add `lifeSpace` to tasks (indexed so Kurz-notiert can query
    // orphan-per-lifespace efficiently). Backfill strategy:
    //   1. task.projectId resolves to a project → task.lifeSpace = project.lifeSpace
    //   2. orphan or project missing/soft-deleted → 'personal'
    // See also supabase/migrations/023_add_task_life_space.sql (mirrors this).
    db.version(16).stores({
      tasks: 'id, title, status, priority, category, lifeSpace, projectId, sectionId, userId, cloudId, startDate, dueDate, completedAt, createdAt, updatedAt, lastModified, synced, parentTaskId, order',
    }).upgrade(async tx => {
      const projects = await tx.table('projects').toArray()
      const projectLifeSpace = new Map<string, string>(
        projects.filter(p => !p.deletedAt).map(p => [p.id, p.lifeSpace]),
      )
      return migrationWrite(() => tx.table('tasks').toCollection().modify(task => {
        if (task.lifeSpace) return
        const fromProject = task.projectId ? projectLifeSpace.get(task.projectId) : undefined
        task.lifeSpace = fromProject ?? 'personal'
      }))
    })

    // Version 17: Retire the per-lifespace `quick-notes` system projects.
    // Kurz-notiert becomes "orphan tasks with matching life_space" — no
    // system project needed. Unlink all tasks that referenced a quick-notes
    // system project, then soft-delete the projects themselves so sync
    // propagates the removal. Mirrors supabase/migrations/024.
    /*
      **Ohne `migrationWrite` — mit Absicht.** Die anderen Migrationen tragen
      Felder nach, die es noch nicht gab; das ist keine Entscheidung des
      Nutzers und gehört darum nicht in die Sync-Buchführung. Diese hier
      LÖSCHT die Quick-Notes-Systemprojekte und hängt ihre Aufgaben ab — eine
      inhaltliche Änderung, die auf die anderen Geräte gehört. Sie setzt
      `synced`/`deletedAt` unten selbst, damit der Push sie mitnimmt.
    */
    db.version(17).stores({}).upgrade(async tx => {
      const systemProjects = await tx.table('projects')
        .filter(p => p.systemKind === 'quick-notes' && !p.deletedAt)
        .toArray()
      if (systemProjects.length === 0) return
      const systemIds = new Set(systemProjects.map(p => p.id))
      await tx.table('tasks').toCollection().modify(task => {
        if (task.projectId && systemIds.has(task.projectId)) {
          task.projectId = null
        }
      })
      const now = new Date()
      await tx.table('projects').toCollection().modify(project => {
        if (systemIds.has(project.id)) {
          project.deletedAt = now
          project.updatedAt = now
          project.lastModified = now
          project.synced = false
        }
      })
    })

    // Version 18: Add goals table (Ziele feature). A Goal is a separate
    // entity from Task — see CONTEXT.md. Indexed on lifeSpace (page query),
    // cloudId + synced (sync-pull duplicate guard), createdAt (sort order).
    db.version(18).stores({
      goals: 'id, lifeSpace, userId, cloudId, createdAt, synced, deletedAt',
    })

    // Version 19: Add habits + habitLogs tables (Gewohnheiten feature).
    // habits indexed on lifeSpace (page query), cloudId + synced (sync-pull
    // duplicate guard), createdAt, deletedAt, pausedAt. habitLogs uses a
    // compound [habitId+date] index for upsert/uniqueness + per-habit streak reads.
    db.version(19).stores({
      habits: 'id, lifeSpace, userId, cloudId, createdAt, synced, deletedAt, pausedAt',
      habitLogs: 'id, habitId, date, [habitId+date], userId, cloudId, synced, deletedAt',
    })

    // Version 20: Add rewardUsages ("Zuletzt benutzt" in the reward picker).
    //
    // The row id IS the catalogue reward id — deliberately, not an accident.
    // The catalogue ships with the app, so both devices derive the same id for
    // the same reward; the sync engine matches on `local_id` and last-write-wins
    // settles `usedAt`. That removes the natural-key duplication problem that
    // forced name-based dedup for tags and projects (checklist point 13).
    //
    // Task.rewardId itself needs no schema bump — unindexed field, and Dexie
    // only versions indexes.
    db.version(20).stores({
      rewardUsages: 'id, usedAt, userId, cloudId, synced, deletedAt',
    })

    // Version 21: Add userPreferences — the synced key/value store for UI
    // settings. Same natural-key trick as v20: the row id IS the preference
    // key, so two devices derive the same id for the same setting and LWW
    // settles the value. `key` is not indexed separately — it equals `id`.
    //
    // The older `settings` table stays as-is: it holds device-local and
    // pre-login state (storageMode, lastSyncTime) that must NOT sync.
    db.version(21).stores({
      userPreferences: 'id, userId, cloudId, synced, deletedAt',
    })

    // Version 22: Add subtasks — the checklist under a task.
    //
    // Its own table, not `parentTaskId` rows in `tasks`. A subtask is a title
    // and a tick; putting it in `tasks` would mean teaching all 48 existing
    // task queries (Heute, Demnächst, project progress, backup, sync-repair)
    // to filter it out, and every one that was missed would leak checklist
    // items into task lists.
    //
    // `taskId` is indexed because every read is "the subtasks of this task".
    db.version(22).stores({
      subtasks: 'id, taskId, userId, cloudId, synced, deletedAt',
    })

    // Version 23: Add canvasNotes — Notizen & Notizen on a project's
    // Projekt-Canvas (see .claude/tasks/project-canvas.md, ADR 0004). Own
    // table, not a Task variant: carries geometry a Task has no use for.
    // Indexed on projectId because every read is "the notes of this project".
    db.version(23).stores({
      canvasNotes: 'id, projectId, userId, cloudId, synced, deletedAt',
    })

    // Version 24: Backfill canvasPanelState/canvasSplitRatio=null on existing
    // projects. Unindexed (app only writes/reads them, doesn't query by them),
    // same pattern as isSystem/systemKind in version 13.
    db.version(24).stores({}).upgrade(tx => {
      return migrationWrite(() => tx.table('projects').toCollection().modify(project => {
        if (!('canvasPanelState' in project)) project.canvasPanelState = null
        if (!('canvasSplitRatio' in project)) project.canvasSplitRatio = null
      }))
    })

    // Version 25: `taskId` wird auf canvasNotes indiziert, und
    // `convertedContent` kommt als (unindiziertes) Feld dazu.
    //
    // Der Index trägt eine einzige, aber unumgängliche Abfrage: Wird eine
    // Aufgabe gelöscht, muss die **Verbindung** jedes darauf zeigenden
    // Notiz gelöst werden (db.deleteTask). Ohne ihn wäre das ein
    // Tabellendurchlauf JE gelöschter Aufgabe — bei einer Mehrfachlöschung
    // also einer pro Zeile.
    //
    // `convertedContent` braucht keinen eigenen Schritt (Dexie versioniert nur
    // Indizes); der Backfill setzt es auf bestehenden Zeilen auf null, damit
    // niemand `undefined` gegen `content` vergleicht.
    db.version(25).stores({
      canvasNotes: 'id, projectId, taskId, userId, cloudId, synced, deletedAt',
    }).upgrade(tx => {
      return migrationWrite(() => tx.table('canvasNotes').toCollection().modify(note => {
        if (!('convertedContent' in note)) note.convertedContent = null
      }))
    })

    // Version 26: `showAuthor` auf bestehenden Zeilen auf `false` setzen.
    //
    // Unindiziert (nichts fragt danach), der Backfill dient allein der
    // Klarheit: `undefined` ist keine gültige Antwort auf „zeigt sie ihren
    // Autor?", und ein `!note.showAuthor` verschleierte den Unterschied
    // zwischen „aus" und „nie gesetzt".
    db.version(26).stores({}).upgrade(tx => {
      return migrationWrite(() => tx.table('canvasNotes').toCollection().modify(note => {
        if (!('showAuthor' in note)) note.showAuthor = false
      }))
    })

    // Version 27: `font` auf bestehenden Zeilen auf `null` setzen.
    //
    // Unindiziert wie `showAuthor` in v26, und aus demselben Grund
    // nachgetragen: `null` ist die gültige Antwort auf „welche Schrift?" und
    // heißt **Standard**; `undefined` hieße gar nichts.
    db.version(27).stores({}).upgrade(tx => {
      return migrationWrite(() => tx.table('canvasNotes').toCollection().modify(note => {
        if (!('font' in note)) note.font = null
      }))
    })

    /*
      Version 28: `areaId` auf bestehenden Zeilen auf `null` setzen — sie
      gehören zu keinem **Bereich**, den gab es ja noch nicht.

      **Indiziert**, anders als `showAuthor` und `font`: Danach WIRD gefragt —
      „welche Elemente liegen in diesem Bereich" ist die Frage, die jedes
      Verschieben, Duplizieren und Löschen eines Bereichs stellt.
    */
    db.version(28).stores({
      canvasNotes: 'id, projectId, areaId, taskId, userId, cloudId, synced, deletedAt',
    }).upgrade(tx => {
      return migrationWrite(() => tx.table('canvasNotes').toCollection().modify(note => {
        if (!('areaId' in note)) note.areaId = null
      }))
    })

    /*
      Version 29: `tape` auf bestehenden Zeilen auf `null` setzen — der
      **Klebestreifen**, den es noch nicht gab.

      **Unindiziert** wie `showAuthor` (v26) und `font` (v27), und aus
      demselben Grund nachgetragen: `null` ist die gültige Antwort auf „welcher
      Streifen?" und heißt **nicht geklebt**; `undefined` hieße gar nichts.
      Danach wird auch nichts gefragt — wer klebt, entscheidet sich beim
      Zeichnen jedes einzelnen Zettels, nicht in einer Abfrage über die
      Tabelle.
    */
    db.version(29).stores({}).upgrade(tx => {
      return migrationWrite(() => tx.table('canvasNotes').toCollection().modify(note => {
        if (!('tape' in note)) note.tape = null
      }))
    })

  /*
    **Die Arten heißen, wie sie in der Oberfläche heißen** (ADR 0014):
    `'sticky'` → `'note'`, `'text'` → `'memo'`. `'area'` bleibt.

    Der Grund ist ein künftiger: `'text'` ist der Name, den ein echtes
    Text-Element brauchen wird — freier Text ohne Rahmen und Hintergrund. Bliebe
    das Memo darauf sitzen, wäre der naheliegendste Name für immer besetzt.

    Ein harter Schnitt, kein Nebeneinander: Die Werte stehen nach dieser
    Migration nirgends mehr, weder hier noch in der Cloud (siehe
    `20260906120000_canvas_notes_kind_rename.sql`). Ein nachsichtiges Lesen
    daneben wäre Code für einen Fall, den es nicht mehr gibt.
  */
  db.version(30).stores({}).upgrade(tx => {
    return migrationWrite(() => tx.table('canvasNotes').toCollection().modify(note => {
      if (note.kind === 'sticky') note.kind = 'note'
      else if (note.kind === 'text') note.kind = 'memo'
    }))
  })

  /*
    **Die Neigung** — jedes bestehende Element liegt gerade.

    `stores({})` bleibt leer: `rotation` bekommt keinen Index. Danach wird nie
    gesucht und nie sortiert; die Neigung wird gelesen, wenn ein Zettel
    gezeichnet wird, und geschrieben, wenn eine Hand ihn dreht.

    Nachgetragen und nicht beim Lesen abgefangen — anders als beim `tape` (v29),
    wo `null` eine Bedeutung trägt: `rotation` ist eine ZAHL, und `undefined`
    liefe durch jede Rechnung als `NaN` weiter, bis irgendwo `rotate: NaNdeg`
    stünde. Eine 0 in der Zeile ist die ehrlichere Antwort.
  */
  db.version(31).stores({}).upgrade(tx => {
    return migrationWrite(() => tx.table('canvasNotes').toCollection().modify(note => {
      if (typeof note.rotation !== 'number') note.rotation = 0
    }))
  })

  /*
    **Die Wiederholung** — jede bestehende Aufgabe ist einmalig (`null`).

    `stores({})` bleibt leer: `recurrence` wird nie gesucht, nur an der Zeile
    gelesen. Nachgetragen wie `pausedAt` (v14), damit `'recurrence' in task`
    überall wahr ist und `normalizeTask` nur noch der Sync-/Undo-Fall bleibt.
    ADR 0021.
  */
  db.version(32).stores({}).upgrade(tx => {
    return migrationWrite(() => tx.table('tasks').toCollection().modify(task => {
      if (!('recurrence' in task)) task.recurrence = null
    }))
  })

  /*
    **Die Fokuszeit** — `timeEntries` wird sync-fähig (ADR 0025).

    Die Tabelle steht seit v2 im Schema und ist auf jedem Gerät leer: Ihre
    `createTimeEntry` hatte nie einen Aufrufer. Darum **keine Migration** und
    kein `migrationWrite` — es gibt nichts nachzutragen, nur neue Indizes:
    `userId`, `cloudId`, `synced` und `deletedAt` für den Sync, wie bei jeder
    anderen sync-fähigen Tabelle.

    **`endTime` fällt aus dem Index.** Die alte Abfrage `where('endTime')
    .equals(null)` konnte nie etwas finden — IndexedDB indiziert `null` nicht,
    und Dexie wirft darauf sogar. Der laufende Abschnitt wird stattdessen über
    `startTime` rückwärts gesucht: Es gibt höchstens einen, und er ist immer
    der jüngste.

    `duration`, `description` und `isManual` verschwinden aus dem Modell, ohne
    dass hier etwas geschieht — Dexie versioniert nur Indizes, und indiziert
    war keines der drei.
  */
  db.version(33).stores({
    timeEntries: 'id, taskId, startTime, userId, cloudId, synced, deletedAt',
  })

  /*
    **Der Text** (`kind: 'text'`) — Schriftgröße und Ausrichtung auf
    bestehenden Zeilen auf `null` setzen.

    Unindiziert wie `tape` (v29) und aus demselben Grund nachgetragen: `null`
    ist bei jeder anderen Art die gültige Antwort („keine eigene Größe“,
    „linksbündig“). Geklammert in `migrationWrite`, sonst stünden danach alle
    Zeilen als ungesynct da (siehe dort).
  */
  db.version(34).stores({}).upgrade(tx => {
    return migrationWrite(() => tx.table('canvasNotes').toCollection().modify(note => {
      if (!('fontSize' in note)) note.fontSize = null
      if (!('textAlign' in note)) note.textAlign = null
    }))
  })

  /*
    **Vorn und hinten** (`zOrder`) — auf bestehenden Zeilen `null`, also „nach
    Alter": Sie behalten genau die Reihenfolge, die sie bisher hatten.
    Unindiziert; geklammert in `migrationWrite` wie v34.
  */
  db.version(35).stores({}).upgrade(tx => {
    return migrationWrite(() => tx.table('canvasNotes').toCollection().modify(note => {
      if (!('zOrder' in note)) note.zOrder = null
    }))
  })

  /*
    **Pinnwand und Aufgaben einmal ganz neu holen** (gemeldet 2026-09-24).

    Die Versionen davor tragen neue Spalten nach (`zOrder` v35, `fontSize`/
    `textAlign` v34, `recurrence` v32 …) — mit `null`, und der Pull-Merker blieb
    stehen. Eine Zeile, die ein anderes Gerät VOR diesem Update geändert hat,
    kam so nie wieder: Die alte Version hatte sie schon geholt, das neue Feld
    übersehen und den Merker dahinter gerückt. Bearbeitete man sie danach hier,
    schickte der Push die ganze Zeile mit `null` hoch und löschte den Wert auch
    auf den anderen Geräten („Ganz nach vorn" rutschte überall nach hinten).

    Ohne Merker holt der nächste Sync die ganze Tabelle; die Cloud-Zeile ist
    neuer als die lokale (die Nachträge oben rühren `updatedAt` nicht an) und
    gewinnt. Das heilt auch Geräte, die v35 schon hinter sich haben.
  */
  db.version(36).stores({}).upgrade(tx => resetPullCursors(tx, ['lastCanvasNoteSyncTime', 'lastSyncTime']))

  /*
    **Projekt-Icon und gestalteter Titel** (`icon`, `iconColor`, `nameHtml`,
    ADR 0030) — auf bestehenden Projekten `null`: Standard-Icon, Titel = `name`.
    Unindiziert wie v24; der Pull-Merker der Projekte wird zurückgesetzt, damit
    ankommt, was ein anderes Gerät schon gesetzt hat (siehe v36).
  */
  db.version(37).stores({}).upgrade(async tx => {
    await migrationWrite(() => tx.table('projects').toCollection().modify(project => {
      if (!('icon' in project)) project.icon = null
      if (!('iconColor' in project)) project.iconColor = null
      if (!('nameHtml' in project)) project.nameHtml = null
    }))
    await resetPullCursors(tx, ['lastProjectSyncTime'])
  })

  /*
    **Noch einmal alle Merker, deren Spalten v35–v37 nachgetragen haben**
    (gemeldet 2026-09-26). Der Pull verwarf bis dahin eine Zeile, die auf die
    Millisekunde so alt war wie die lokale — genau die, die eine ältere
    App-Version schon geholt und dabei die neue Spalte übersehen hatte. Seit
    dem Fix in `sync-pull.ts` gilt die Cloud bei gleichem Stand, wenn sie sich
    unterscheidet; dieser Durchgang lässt ihn die betroffenen Zeilen einmal
    neu ansehen (Projekt-Icons, gestaltete Titel, „Ganz nach vorn").
  */
  db.version(38).stores({}).upgrade(tx => resetPullCursors(tx, ['lastProjectSyncTime', 'lastCanvasNoteSyncTime', 'lastSyncTime']))

  /*
    **Herkunft eines Imports** (`importedFrom`, `todoist:<id>`) — auf
    bestehenden Zeilen `null`: Sie stammen aus keinem Import.

    **Indiziert**: Ein Import schlägt für Hunderte Zeilen auf einmal nach, ob
    er sie schon angelegt hat (docs/concept/todoist-import.md). Geklammert in
    `migrationWrite` wie v34; die Pull-Merker der fünf Tabellen werden
    zurückgesetzt wie v37 (siehe v36).
  */
  db.version(39).stores({
    tasks: 'id, title, status, priority, category, lifeSpace, projectId, sectionId, userId, cloudId, startDate, dueDate, completedAt, createdAt, updatedAt, lastModified, synced, parentTaskId, order, importedFrom',
    subtasks: 'id, taskId, userId, cloudId, synced, deletedAt, importedFrom',
    projects: 'id, name, status, lifeSpace, userId, cloudId, order, createdAt, updatedAt, lastModified, synced, importedFrom',
    sections: 'id, projectId, cloudId, order, synced, deletedAt, importedFrom',
    tags: 'id, name, usageCount, userId, cloudId, deletedAt, createdAt, updatedAt, lastModified, synced, importedFrom',
  }).upgrade(async tx => {
    for (const table of ['tasks', 'subtasks', 'projects', 'sections', 'tags'] as const) {
      await migrationWrite(() => tx.table(table).toCollection().modify(row => {
        if (!('importedFrom' in row)) row.importedFrom = null
      }))
    }
    await resetPullCursors(tx, ['lastSyncTime', 'lastSubtaskSyncTime', 'lastProjectSyncTime', 'lastSectionSyncTime', 'lastTagSyncTime'])
  })

  /*
    **Unteraufgaben werden Aufgaben** (T14 #179). `tasks` bekommt in der Cloud
    `parent_task_id`/`parent_local_id`; lokal gibt es `parentTaskId` schon, also nur
    den Pull-Merker zurücksetzen (Regel oben, v36).

    `adoptCloudId` indiziert: Ein Pull fragt bei jeder neuen Aufgaben-Zeile nach, ob
    hier eine umgewandelte Unteraufgabe genau diese Cloud-Kennung übernehmen will.

    Die Umwandlung selbst läuft NICHT hier, sondern in `convertLegacySubtasks`: Sie
    schreibt vorher eine Sicherungsdatei, und die darf eine Dexie-Transaktion nicht
    abwarten.
  */
  db.version(40).stores({
    tasks: 'id, title, status, priority, category, lifeSpace, projectId, sectionId, userId, cloudId, startDate, dueDate, completedAt, createdAt, updatedAt, lastModified, synced, parentTaskId, order, importedFrom, adoptCloudId',
  }).upgrade(async tx => {
    await resetPullCursors(tx, ['lastSyncTime'])
  })

  /*
    **Kommentare an Aufgaben** (`taskComments`, #182). Eine neue Tabelle, leer —
    keine Zeile zu migrieren, kein Pull-Merker zurückzusetzen (der der
    Kommentare existiert noch nicht). `taskId` für die Liste einer Aufgabe und
    die Kaskade beim Löschen, `postedAt` für die Reihenfolge.
  */
  db.version(41).stores({
    taskComments: 'id, taskId, postedAt, userId, cloudId, synced, deletedAt, importedFrom',
  })

  /*
    **Kommentare an Projekten** (`projectComments`, #184). Wie v41 eine neue, leere
    Tabelle — nichts zu migrieren, kein Pull-Merker zurückzusetzen. `projectId` für die
    Liste eines Projekts und die Kaskade beim Löschen.
  */
  db.version(42).stores({
    projectComments: 'id, projectId, postedAt, userId, cloudId, synced, deletedAt, importedFrom',
  })

  /*
    **Erinnerungen** (`taskReminders`, Plan task-reminders). Wie v41 eine neue, leere
    Tabelle — nichts zu migrieren, kein Pull-Merker zurückzusetzen. `taskId` für die
    Erinnerungen einer Aufgabe, die Kaskade und den Zeitplan.
  */
  db.version(43).stores({
    taskReminders: 'id, taskId, userId, cloudId, synced, deletedAt, importedFrom',
  })

  /*
    **Unterprojekte** (`Project.parentId`, #185) — auf bestehenden Projekten `null`: Sie
    stehen oben im Lebensbereich. Indiziert, weil Löschen, Lebensbereich-Wechsel und der
    Pull die Kinder eines Projekts nachschlagen. Geklammert in `migrationWrite` wie v39,
    und der Pull-Merker der Projekte wird zurückgesetzt (Regel oben, v36).
  */
  db.version(44).stores({
    projects: 'id, name, status, lifeSpace, userId, cloudId, order, createdAt, updatedAt, lastModified, synced, importedFrom, parentId',
  }).upgrade(async tx => {
    await migrationWrite(() => tx.table('projects').toCollection().modify(project => {
      if (!('parentId' in project)) project.parentId = null
    }))
    await resetPullCursors(tx, ['lastProjectSyncTime'])
  })

  /*
    **Beschreibung am Abschnitt** (`Section.description`, #192) — auf bestehenden Abschnitten
    `null`. Unindiziert wie v37; geklammert in `migrationWrite`, und der Pull-Merker der
    Abschnitte wird zurückgesetzt (Regel oben, v36).
  */
  db.version(45).stores({}).upgrade(async tx => {
    await migrationWrite(() => tx.table('sections').toCollection().modify(section => {
      if (!('description' in section)) section.description = null
    }))
    await resetPullCursors(tx, ['lastSectionSyncTime'])
  })

  /*
    **Archivierte Abschnitte** (`Section.archivedAt`, #193) — auf bestehenden Abschnitten
    `null` (aktiv). Unindiziert wie v45: Die Abschnitte eines Projekts sind wenige, gefiltert
    wird im Speicher. Geklammert in `migrationWrite`, Pull-Merker zurück (Regel oben, v36).
  */
  db.version(46).stores({}).upgrade(async tx => {
    await migrationWrite(() => tx.table('sections').toCollection().modify(section => {
      if (!('archivedAt' in section)) section.archivedAt = null
    }))
    await resetPullCursors(tx, ['lastSectionSyncTime'])
  })

  /*
    **Favoriten** (`favoriteOrder` an Projekten und Aufgaben, #210). Aufgaben bekommen einen
    Index: Dexie nimmt `null`/fehlende Werte nicht in den Index auf, die Abfrage der Favoriten
    liest so nur Favoriten statt aller Aufgaben. Projekte sind wenige — unindiziert.
    **Nichts nachgetragen:** Ein fehlender Wert heißt „kein Favorit“ (`?? null` überall), und ein
    `modify` über alle Aufgaben kostete beim Start spürbar. Die Pull-Merker beider Tabellen gehen
    trotzdem zurück (Regel oben, v36): Was ein anderes Gerät schon favorisiert hat, muss ankommen.
  */
  db.version(47).stores({
    tasks: 'id, title, status, priority, category, lifeSpace, projectId, sectionId, userId, cloudId, startDate, dueDate, completedAt, createdAt, updatedAt, lastModified, synced, parentTaskId, order, importedFrom, adoptCloudId, favoriteOrder',
  }).upgrade(async tx => {
    await resetPullCursors(tx, ['lastSyncTime', 'lastProjectSyncTime'])
  })

  /*
    **Erledigt-Zähler** und **Vertagen** (`completedCount`, `postponedCount` an Aufgaben, #198).
    Unindiziert — niemand fragt danach ab. **Nichts nachgetragen:** Ein fehlender Wert zählt als 0
    (`?? 0` in `lib/db/taskCounters`). Der Pull-Merker der Aufgaben geht zurück (Regel oben, v36):
    Was ein anderes Gerät schon gezählt hat, muss ankommen.
  */
  db.version(48).stores({}).upgrade(async tx => {
    await resetPullCursors(tx, ['lastSyncTime'])
  })

  /*
    **Herkunft an Zielen und Gewohnheiten** (`importedFrom`, #269) — Testdaten tragen
    `testdata:<lauf>:<n>`, damit „Testdaten entfernen“ kein echtes „Mehr lesen“ trifft. Auf
    bestehenden Zeilen `null`. Indiziert wie v39; geklammert in `migrationWrite`, Pull-Merker
    beider Tabellen zurück (Regel oben, v36).
  */
  db.version(49).stores({
    goals: 'id, lifeSpace, userId, cloudId, createdAt, synced, deletedAt, importedFrom',
    habits: 'id, lifeSpace, userId, cloudId, createdAt, synced, deletedAt, pausedAt, importedFrom',
  }).upgrade(async tx => {
    for (const table of ['goals', 'habits'] as const) {
      await migrationWrite(() => tx.table(table).toCollection().modify(row => {
        if (!('importedFrom' in row)) row.importedFrom = null
      }))
    }
    await resetPullCursors(tx, ['lastGoalSyncTime', 'lastHabitSyncTime'])
  })

  /*
    **Kurz notiert: gedachte Gruppen als Abschnitt** (#304). Bis zum Fix speicherte eine neue
    Aufgabe dort die Kennung von „Letzte 7 Tage“ oder „Älter“ als `sectionId` — eine Gruppe ohne
    Dexie-Zeile. Hier wird sie `null`. Die Kennungen stehen als Literal: `lib/` importiert nicht
    aus `features/`, und eine Migration hält den Stand ihres Tages fest.
    **Kein Zurücksetzen der Pull-Merker:** Es kommt keine Spalte dazu, und in der Cloud steht schon
    `null` (`resolveCloudSectionId` findet keine Zeile). Geklammert in `migrationWrite`.
  */
  db.version(50).stores({}).upgrade(async tx => {
    await migrationWrite(() => tx.table('tasks')
      .where('sectionId').anyOf(['__quick_notes_recent__', '__quick_notes_older__'])
      .modify(task => { task.sectionId = null }))
  })
}

/**
 * Setzt Pull-Merker zurück: Der nächste Sync holt die Tabellen dahinter ganz neu.
 *
 * **Pflicht in jeder Version, die einer synchronisierten Tabelle eine Spalte
 * nachträgt** — mit dem Merker dieser Tabelle (`settingsKey` ihres
 * Pull-Deskriptors). Sonst kommt, was ein anderes Gerät vor dem Update gesetzt
 * hat, hier nie an, und der nächste Push überschreibt es mit `null` (siehe
 * v36). Der Wächter `tests/unit/lib/db/newColumnCursorGuard.test.ts` prüft das.
 */
export function resetPullCursors(tx: Transaction, keys: readonly string[]): Promise<number> {
  return migrationWrite(() => tx.table('settings').where('key').anyOf([...keys]).delete())
}
